import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, doc, setDoc, runTransaction } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL } from "./firebaseUtils.js";
import { Save, ArrowLeft, User, MapPin, Plus, Trash2, X, Edit2, Phone, Mail, FileText, UserCheck, Copy, Loader2, Paperclip, Eye } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useClickOutside from './hooks/useClickOutside.js';
import useUnsavedChanges from './hooks/useUnsavedChanges.js';
import Spinner from './Spinner.js';
import { NewCustomerModal, AddAddressModal } from './CustomerModals.js';

const SALES_TEAM = [
  "Rajesh Soni", "Sanjay Kumawat", "Deepak Yadav", "Deepak Kumar", 
  "Rohit Soni", "Prinka Sharma", "Ankit Kumar Jangid", "Piyush Agarwal"
];

const formatAddress = (addr) => {
  if (!addr) return 'No Address Selected';
  return `${addr.attention ? addr.attention + ', ' : ''}${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
};

const getFirmPrefix = (firmId) => {
  if (firmId.startsWith('nexrise')) return 'NIIPL';
  if (firmId.startsWith('jagdish')) return 'JE';
  if (firmId.startsWith('cloudnine')) return 'C9';
  return 'GEN';
};

const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
        const decodedUrl = decodeURIComponent(url);
        const pathParts = decodedUrl.split('?')[0].split('/');
        const fileNameWithPrefix = pathParts[pathParts.length - 1];
        const underscoreIndex = fileNameWithPrefix.indexOf('_');
        if (underscoreIndex !== -1) {
            if (/^\d{13,}/.test(fileNameWithPrefix)) {
                return fileNameWithPrefix.substring(underscoreIndex + 1);
            }
            const lastUnderscoreIndex = fileNameWithPrefix.lastIndexOf('_');
            if (lastUnderscoreIndex !== -1 && /\d{13,}/.test(fileNameWithPrefix.substring(lastUnderscoreIndex + 1))) {
                return fileNameWithPrefix.substring(0, lastUnderscoreIndex);
            }
        }
        return fileNameWithPrefix;
    } catch (e) {
        return 'Attached File';
    }
};


const CreateOrder = ({ db }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const customerSearchRef = useClickOutside(() => setShowCustSuggestions(false));

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  // --- BOM STATE ---
  const [boms, setBoms] = useState([]);
  const [showBomModal, setShowBomModal] = useState(false);
  const [currentBom, setCurrentBom] = useState(null); 
  const [bomSearchTerm, setBomSearchTerm] = useState('');
  const [bomFilteredProducts, setBomFilteredProducts] = useState([]);
  const [showBomSearchSuggestions, setShowBomSearchSuggestions] = useState(false);
  const bomSearchRef = useClickOutside(() => setShowBomSearchSuggestions(false));

  // --- 1. MAIN FORM STATE ---
  const [formData, setFormData] = useState({
    firmId: '',
    date: new Date().toISOString().split('T')[0],
    orderType: 'Sales', 
    salesPerson: '',
    subject: '',
    customerID: '',
    billingAddress: null,
    shippingAddress: null,
    projectName: '',
    projectLocation: null,
    quoteNumber: '',
    quoteDate: '',
    poNumber: '',
    poDate: '',
    orderValue: 0,
    paymentStatus: 'RECEIVED',
    salesOrderNo: '',
    proformaInvoiceNo: '',
    customerPONo: '',
    rentalAgreementNo: '',
    rentalPeriodStart: '',
    rentalPeriodEnd: '',
    monthlyRentalValue: 0,
    workOrderNumber: '',
    woDate: '',
    rentalAgreementReceived: false,
    backgroundVerificationDone: false,
    backgroundVerificationDoneBy: '',
    securityChequeReceived: false,
    rentalTenureMonths: 0,
    logisticsDetails: '',
    installationDetails: '',
    quoteFileUrl: '',
    poFileUrl: '',
    workOrderFileUrl: '',
    rentalAgreementFileUrl: '',
    securityChequeFileUrl: '',
  });

  const [materialChecklistState, setMaterialChecklistState] = useState({
    stickers: false,
    laserMarking: false,
    materialWrapping: false,
    materialInstructions: ''
  });

  // --- DATA LOAD STATE ---
  const [firms, setFirms] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [bomDatabase, setBomDatabase] = useState([]);
  
  // --- 3. UI STATE ---
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustSuggestions, setShowCustSuggestions] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // --- FILE MODAL STATE ---
  const [showFileModal, setShowFileModal] = useState(false);
  const [activeFileField, setActiveFileField] = useState(null);
  const [activeFileLabel, setActiveFileLabel] = useState('');
  
  // --- 4. MODAL STATES ---
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('billing'); 
  const [showModal, setShowModal] = useState(false); 
  const [customerDataToEdit, setCustomerDataToEdit] = useState(null);
  const [addressDataToEdit, setAddressDataToEdit] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const isInitialRender = useRef(true);

  const setSafeToNavigate = useUnsavedChanges(isDirty);

  // Track changes to form data
  useEffect(() => {
    if (loadingData) return;
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    setIsDirty(true);
  }, [formData, boms, materialChecklistState, loadingData]);

  // --- INITIAL DATA LOADING ---
  useEffect(() => {
    const loadData = async () => {
      const firmSnap = await getDocs(collection(db, "firms"));
      const firmsList = firmSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFirms(firmsList);

      const custSnap = await getDocs(collection(db, "customers"));
      const customersList = custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllCustomers(customersList);

      const bomSnap = await getDocs(collection(db, "bom_products"));
      const bomList = bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBomDatabase(bomList);

      if (location.state?.clonedOrder) {
        const cloned = location.state.clonedOrder;
        setFormData({
          ...cloned,
          date: new Date().toISOString().split('T')[0], 
        });
        setBoms(cloned.boms || []);
        setMaterialChecklistState(cloned.materialChecklist || {
          stickers: false,
          laserMarking: false,
          materialWrapping: false,
          materialInstructions: ''
        });
        
        const customer = customersList.find(c => c.id === cloned.customerID);
        if (customer) {
          setCustomerSearchTerm(customer.display_name);
        }
      } else {
        if (firmsList.length > 0) {
          setFormData(prev => ({ ...prev, firmId: firmsList[0].id }));
        }
      }
      setLoadingData(false);
    };
    loadData();
  }, [db, location.state]);

  // --- CUSTOMER SEARCH LOGIC ---
  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(term));
      setFilteredCustomers(matches);
      setShowCustSuggestions(true);
    } else {
      setFilteredCustomers(allCustomers);
      setShowCustSuggestions(true);
    }
  };

  const handleFirmChange = (e) => {
    const firmId = e.target.value;
    setFormData(p => ({ ...p, firmId }));
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({ 
      ...prev, 
      customerID: customer.id,
      billingAddress: customer.addresses?.find(a => a.address_type === 'Billing') || null,
      shippingAddress: customer.addresses?.find(a => a.address_type === 'Shipping') || null,
    }));
    setCustomerSearchTerm(customer.display_name);
    setShowCustSuggestions(false);
  };
  
  const selectedCustomer = allCustomers.find(c => c.id === formData.customerID);

  const handleAddressSelect = (type, value) => {
    if (value === 'new') {
      setAddressModalType(type);
      setShowAddressModal(true);
      return;
    }

    const address = selectedCustomer.addresses.find(a => a.id === value);
    if (type === 'billing') {
      setFormData(prev => ({ ...prev, billingAddress: address }));
    } else {
      setFormData(prev => ({ ...prev, shippingAddress: address }));
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const storageRef = ref(storage, `uploads/${fieldName}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, [fieldName]: url }));
    } catch (error) {
      console.error("Upload failed", error);
      alert("Upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const openFileModal = (fieldName, label) => {
      setActiveFileField(fieldName);
      setActiveFileLabel(label);
      setShowFileModal(true);
  };

  const renderFileUpload = (fieldName, label) => (
    <div>
        <label style={labelStyle}>{label}</label>
        <button 
            onClick={() => openFileModal(fieldName, label)}
            style={{
                ...inputStyle, 
                textAlign: 'left', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: formData[fieldName] ? '#f0fdf4' : 'white',
                borderColor: formData[fieldName] ? '#22c55e' : '#d1d5db'
            }}
        >
            <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                {formData[fieldName] ? <Eye size={16} color="#16a34a"/> : <Paperclip size={16} color="#6b7280"/>}
                {formData[fieldName] ? 'View Attached File' : 'Attach File'}
            </span>
            {formData[fieldName] && <span style={{fontSize: '10px', color: '#16a34a', fontWeight: 'bold'}}>UPLOADED</span>}
        </button>
    </div>
  );

  // --- SAVE ORDER ---
  const saveOrder = async (orderStatus) => {
    if (orderStatus === 'confirmed_order') {
      const confirmMessage = "Are you sure you want to CONFIRM this order? \n\nPlease verify all details (Firm, Customer, Items, Value) before proceeding. \n\nOnce confirmed, this order will be locked and CANNOT be edited. Do you wish to continue?";
      if (!window.confirm(confirmMessage)) return;
    }

    const requiredFields = {
      firmId: 'From (Firm)',
      date: 'Date',
      salesPerson: 'Salesperson',
      orderType: 'Order Type',
      customerID: 'Customer',
    };

    const missingFieldKeys = Object.keys(requiredFields).filter(field => {
      const value = formData[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (missingFieldKeys.length > 0) {
      const missingFieldNames = missingFieldKeys.map(key => requiredFields[key]).join(', ');
      alert(`Please fill out all required fields: ${missingFieldNames}.`);
      return;
    }

    setIsSaving(true);
    const bomsToSave = boms.map(bom => ({
      ...bom,
      components: bom.components.map(comp => {
        return {
          ...comp, 
          qty: Math.round(comp.baseQty * bom.multiplier), 
        };
      })
    }));

    const firmPrefix = getFirmPrefix(formData.firmId);
    const counterKey = 'order_' + firmPrefix.toLowerCase() + '_count';
    const sequencesRef = doc(db, "counters", "sequences");
    const yearCode = "25-26";

    try {
        const newCounter = await runTransaction(db, async (transaction) => {
            const sequenceDoc = await transaction.get(sequencesRef);
            if (!sequenceDoc.exists()) throw new Error("Counter document does not exist!");
            const currentCount = sequenceDoc.data()[counterKey] || 1000;
            const nextCount = currentCount + 1;
            transaction.update(sequencesRef, { [counterKey]: nextCount });
            return currentCount;
        });

        const orderNumber = `${firmPrefix}/${yearCode}/OB${newCounter}`;
        
        const orderPayload = {
            ...formData,
            orderNumber: orderNumber,
            customerName: selectedCustomer.display_name,
            logisticsDetails: formData.logisticsDetails,
            installationDetails: formData.installationDetails,
            materialChecklist: materialChecklistState,
            boms: bomsToSave, 
            status: orderStatus,
            createdAt: new Date().toISOString()
        };

        await addDoc(collection(db, "orders"), orderPayload);
        alert(`Order Saved Successfully! Number: ${orderNumber}`);
        setIsDirty(false);
        setSafeToNavigate();
        navigate('/orders');
        
    } catch (error) {
        setIsSaving(false);
        console.error("Error saving order or transaction failed: ", error);
        alert("Error saving order: " + error.message);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '10px' : '0 0 100px 0', paddingBottom: '100px' }}>
      
      {isSaving && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner />
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#2563eb' }}>Saving Order...</div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: '20px', gap: isMobile ? '15px' : '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Link to="/orders" style={{ color: '#6b7280' }}><ArrowLeft size={20} /></Link>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>New Order Book</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
          <button onClick={() => saveOrder('draft')} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
            <Save size={18} color="black" /> {isMobile ? 'Draft' : 'Save Draft'}
          </button>
          <button onClick={() => saveOrder('confirmed_order')} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
            <Save size={18} color="black" /> {isMobile ? 'Confirm' : 'Confirm Order'}
          </button>
        </div>
      </div>

      {/* --- ORDER INFORMATION --- */}
      <div style={{ ...cardStyle, padding: isMobile ? '15px' : '20px' }}>
        <h3>Order Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>From (Firm)</label><select name="firmId" value={formData.firmId} onChange={handleFirmChange} style={inputStyle}>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
            <div><label style={labelStyle}>Date</label><input type="date" name="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Order Type</label><select name="orderType" value={formData.orderType} onChange={(e) => setFormData({...formData, orderType: e.target.value})} style={inputStyle}><option>Sales</option><option>Rental</option></select></div>
            <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>Salesperson</label><select name="salesPerson" value={formData.salesPerson} onChange={(e) => setFormData({...formData, salesPerson: e.target.value})} style={inputStyle}><option value="">Select...</option>{SALES_TEAM.map(n => <option key={n} value={n}>{n}</option>)}</select></div>
        </div>
      </div>

      {/* --- CUSTOMER DETAILS --- */}
       <div style={{...cardStyle, marginTop: '20px', padding: isMobile ? '15px' : '20px'}}>
            <h3>Customer Details</h3>
           {/* Search Input */}
           <div style={{ marginBottom: selectedCustomer ? '20px' : '0' }}>
              <label style={labelStyle}><User size={12}/> Select Customer</label>
              <div style={{ position: 'relative' }} ref={customerSearchRef}>
                <input 
                  placeholder="Search by Display Name..." 
                  value={customerSearchTerm}
                  onFocus={() => { setFilteredCustomers(allCustomers); setShowCustSuggestions(true); }}
                  onChange={(e) => handleCustomerSearch(e.target.value)}
                  style={{ ...inputStyle, padding: '12px', fontSize: '15px' }}
                  autoComplete="off"
                />
                {showCustSuggestions && (
                  <div style={{...dropdownStyle, width: '100%'}}>
                    {filteredCustomers.map(cust => (
                      <div key={cust.id} onClick={() => selectCustomer(cust)} style={{...dropdownItemStyle, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <span><strong>{cust.display_name}</strong> - {cust.company_name}</span>
                        {formData.customerID === cust.id && (
                          <button onClick={(e) => { e.stopPropagation(); setShowModal(true); setCustomerDataToEdit(cust); }} style={linkBtnStyle}>
                            <Edit2 size={12}/> Edit
                          </button>
                        )}
                      </div>
                    ))}
                    <div onClick={() => { setShowModal(true); setCustomerDataToEdit(null); setShowCustSuggestions(false); }} style={{ ...dropdownItemStyle, color: '#2563eb', fontWeight: 'bold', borderTop: '1px solid #e5e7eb', display:'flex', alignItems:'center', gap:5 }}>
                      <Plus size={16} /> Add New Customer
                    </div>
                  </div>
                )}
              </div>
           </div>

           {selectedCustomer && (
             <>
               <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                 <h4 style={{margin:0, fontSize:'16px'}}>Customer: {selectedCustomer.display_name}</h4>
                 <button onClick={() => { setShowModal(true); setCustomerDataToEdit(selectedCustomer); }} style={linkBtnStyle}>
                   <Edit2 size={12}/> Edit Details
                 </button>
               </div>
               {/* Project Name and Location */}
               <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', borderTop:'1px solid #e5e7eb', paddingTop:'20px', animation: 'fadeIn 0.3s' }}>
                  <div><label style={labelStyle}>Project Name</label><input type="text" name="projectName" value={formData.projectName} onChange={(e) => setFormData(p => ({...p, projectName: e.target.value}))} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Project Location</label><input type="text" name="projectLocation" value={formData.projectLocation?.address || ''} onChange={(e) => setFormData(p => ({...p, projectLocation: {...p.projectLocation, address: e.target.value}}))} style={inputStyle} /></div>
               </div>

               {/* Billing and Shipping Addresses */}
               <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', paddingTop:'20px', animation: 'fadeIn 0.3s' }}>
                  
                  {/* --- LEFT SIDE: BILLING --- */}
                  <div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                       <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Billing Address</label>
                       <div>
                         {formData.billingAddress && (
                           <button onClick={() => { setAddressDataToEdit(formData.billingAddress); setShowAddressModal(true); setAddressModalType('billing'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                         )}
                       </div>
                    </div>
                    
                    <select 
                      value={formData.billingAddress?.id || ''} 
                      onChange={(e) => handleAddressSelect('billing', e.target.value)}
                      style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                    >
                        <option value="">-- Select Address --</option>
                        {selectedCustomer?.addresses?.filter(a => a.address_type === 'Billing').map(addr => (
                              <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                        ))}
                        <option value="new">+ Add New Address</option>
                    </select>
                    {formData.billingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.billingAddress)}</div>}

                    {/* PURCHASER CONTACT INFO */}
                    <div style={{marginTop: 15}}>
                       <label style={labelStyle}>Purchaser Details</label>
                       <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                          {formData.billingAddress?.attention && (
                            <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                <UserCheck size={12} color="#4b5563"/> {formData.billingAddress.attention}
                            </div>
                          )}
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Phone size={12} color="#4b5563"/> {formData.billingAddress?.phone || selectedCustomer.mobile || '--'}
                          </div>
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Mail size={12} color="#4b5563"/> {formData.billingAddress?.email || selectedCustomer.email || '--'}
                          </div>
                       </div>
                    </div>
                  </div>

                  {/* --- RIGHT SIDE: SHIPPING --- */}
                  <div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                       <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Shipping Address</label>
                       <div>
                         {formData.shippingAddress && (
                           <button onClick={() => { setAddressDataToEdit(formData.shippingAddress); setShowAddressModal(true); setAddressModalType('shipping'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                         )}
                       </div>
                    </div>

                    <select 
                      value={formData.shippingAddress?.id || ''} 
                      onChange={(e) => handleAddressSelect('shipping', e.target.value)}
                      style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                    >
                        <option value="">-- Select Address --</option>
                        {selectedCustomer?.addresses?.filter(a => a.address_type === 'Shipping').map(addr => (
                              <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                        ))}
                        <option value="new">+ Add New Address</option>
                    </select>
                    {formData.shippingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.shippingAddress)}</div>}

                    {/* SITE PERSON CONTACT INFO */}
                    <div style={{marginTop: 15}}>
                       <label style={labelStyle}>Site Person Details</label>
                       <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                          {(formData.shippingAddress?.attention || selectedCustomer.primary_contact?.first_name) && (
                              <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                  <UserCheck size={12} color="#4b5563"/> 
                                  {formData.shippingAddress?.attention || `${selectedCustomer.primary_contact?.first_name || ''} ${selectedCustomer.primary_contact?.last_name || ''}`}
                              </div>
                          )}
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Phone size={12} color="#4b5563"/> {formData.shippingAddress?.phone || selectedCustomer.mobile || selectedCustomer.work_phone || '--'}
                          </div>
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Mail size={12} color="#4b5563"/> {formData.shippingAddress?.email || selectedCustomer.email || '--'}
                          </div>
                       </div>
                    </div>
                  </div>
               </div>
             </>
           )}
      </div>

        {/* --- DYNAMIC ORDER DETAILS SECTION --- */}
        <div style={{...cardStyle, marginTop: '20px', padding: isMobile ? '15px' : '20px'}}>
            <h3>{formData.orderType} Order Details</h3>
            {formData.orderType === 'Sales' ? (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={labelStyle}>Quote Number</label>
                        <input type="text" name="quoteNumber" value={formData.quoteNumber} onChange={(e) => setFormData({...formData, quoteNumber: e.target.value})} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Quote Date</label>
                        <input type="date" name="quoteDate" value={formData.quoteDate} onChange={(e) => setFormData({...formData, quoteDate: e.target.value})} style={inputStyle} />
                    </div>
                    <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
                        {renderFileUpload('quoteFileUrl', 'Upload Quote')}
                    </div>
                    <div>
                        <label style={labelStyle}>PO Number</label>
                        <input type="text" name="poNumber" value={formData.poNumber} onChange={(e) => setFormData({...formData, poNumber: e.target.value})} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>PO Date</label>
                        <input type="date" name="poDate" value={formData.poDate} onChange={(e) => setFormData({...formData, poDate: e.target.value})} style={inputStyle} />
                    </div>
                    <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
                        {renderFileUpload('poFileUrl', 'Upload PO')}
                    </div>
                    <div>
                        <label style={labelStyle}>Order Value</label>
                        <input type="number" name="orderValue" value={formData.orderValue} onChange={(e) => setFormData({...formData, orderValue: parseFloat(e.target.value)})} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Payment Status</label>
                        <select name="paymentStatus" value={formData.paymentStatus} onChange={(e) => setFormData({...formData, paymentStatus: e.target.value})} style={inputStyle}>
                            <option>RECEIVED</option>
                            <option>ON DISPATCH</option>
                            <option>CREDIT (NET 7)</option>
                            <option>CREDIT (NET 15)</option>
                            <option>CREDIT (NET 30)</option>
                            <option>CREDIT (NET 45)</option>
                            <option>CREDIT (NET 60)</option>
                            <option>CREDIT (NET 90)</option>
                        </select>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '20px' }}>
                        <div>
                            <label style={labelStyle}>Quote Number</label>
                            <input type="text" name="quoteNumber" value={formData.quoteNumber} onChange={(e) => setFormData({...formData, quoteNumber: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Quote Date</label>
                            <input type="date" name="quoteDate" value={formData.quoteDate} onChange={(e) => setFormData({...formData, quoteDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
                            {renderFileUpload('quoteFileUrl', 'Upload Quote')}
                        </div>
                        <div>
                            <label style={labelStyle}>Work Order Number</label>
                            <input type="text" name="workOrderNumber" value={formData.workOrderNumber} onChange={(e) => setFormData({...formData, workOrderNumber: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Work Order Date</label>
                            <input type="date" name="woDate" value={formData.woDate} onChange={(e) => setFormData({...formData, woDate: e.target.value})} style={inputStyle} />
                         </div>
                        <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
                            {renderFileUpload('workOrderFileUrl', 'Upload Work Order')}
                        </div>
                    </div>

                    {/* Checkboxes in a single row */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        {/* Rental Agreement */}
                        <div>
                            <input type="checkbox" id="rentalAgreementReceived" name="rentalAgreementReceived" checked={formData.rentalAgreementReceived} onChange={(e) => setFormData({...formData, rentalAgreementReceived: e.target.checked})} style={{ marginRight: '10px' }} />
                            <label htmlFor="rentalAgreementReceived" style={{ fontSize: '14px', fontWeight: 'normal', color: '#374151' }}>Rental Agreement</label>
                            {formData.rentalAgreementReceived && (
                                <div style={{marginTop: '10px'}}>
                                    {renderFileUpload('rentalAgreementFileUrl', 'Upload Agreement')}
                                </div>
                            )}
                        </div>

                        {/* Background Verification */}
                        <div>
                            <input type="checkbox" id="backgroundVerificationDone" name="backgroundVerificationDone" checked={formData.backgroundVerificationDone} onChange={(e) => setFormData({...formData, backgroundVerificationDone: e.target.checked})} style={{ marginRight: '10px' }} />
                            <label htmlFor="backgroundVerificationDone" style={{ fontSize: '14px', fontWeight: 'normal', color: '#374151' }}>BG Verification</label>
                            {formData.backgroundVerificationDone && (
                                <div style={{marginTop: '10px'}}>
                                    <label style={labelStyle}>Done By</label>
                                    <select name="backgroundVerificationDoneBy" value={formData.backgroundVerificationDoneBy} onChange={(e) => setFormData({...formData, backgroundVerificationDoneBy: e.target.value})} style={inputStyle}>
                                        <option value="">Select...</option>
                                        <option>Nitin Ekbote</option>
                                        <option>Satish Pawar</option>
                                        <option>Rohit Soni</option>
                                        <option>Ghanshyam</option>
                                        <option>Rajesh</option>
                                        <option>Ankit</option>
                                        <option>Parmesh</option>
                                        <option>Pranav Machhi</option>
                                        <option>Shahid Ali</option>
                                        <option>Deepak Yadav</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        
                        {/* Security Cheque */}
                        <div>
                            <input type="checkbox" id="securityChequeReceived" name="securityChequeReceived" checked={formData.securityChequeReceived} onChange={(e) => setFormData({...formData, securityChequeReceived: e.target.checked})} style={{ marginRight: '10px' }} />
                            <label htmlFor="securityChequeReceived" style={{ fontSize: '14px', fontWeight: 'normal', color: '#374151' }}>Security Cheque</label>
                            {formData.securityChequeReceived && (
                                <div style={{marginTop: '10px'}}>
                                    {renderFileUpload('securityChequeFileUrl', 'Upload Cheque')}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Rental Tenure, Order Value, Payment Status in a single row */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        <div>
                            <label style={labelStyle}>Tenure (Months)</label>
                            <input type="number" name="rentalTenureMonths" value={formData.rentalTenureMonths} onChange={(e) => setFormData({...formData, rentalTenureMonths: parseFloat(e.target.value)})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Order Value</label>
                            <input type="number" name="orderValue" value={formData.orderValue} onChange={(e) => setFormData({...formData, orderValue: parseFloat(e.target.value)})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
                            <label style={labelStyle}>Payment Status</label>
                            <select name="paymentStatus" value={formData.paymentStatus} onChange={(e) => setFormData({...formData, paymentStatus: e.target.value})} style={inputStyle}>
                                <option>RECEIVED</option>
                                <option>ON DISPATCH</option>
                                <option>CREDIT (NET 7)</option>
                                <option>CREDIT (NET 15)</option>
                                <option>CREDIT (NET 30)</option>
                                <option>CREDIT (NET 45)</option>
                                <option>CREDIT (NET 60)</option>
                                <option>CREDIT (NET 90)</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* --- LOGISTICS, INSTALLATION, MATERIAL CHECKLIST SECTIONS (3-Column Layout) --- */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '20px', marginTop: '20px' }}>
            {/* --- LOGISTICS --- */}
            <div style={{...cardStyle, padding: isMobile ? '15px' : '20px'}}>
                <h3>Logistics</h3>
                <textarea 
                    name="logisticsDetails" 
                    value={formData.logisticsDetails} 
                    onChange={(e) => setFormData({...formData, logisticsDetails: e.target.value})} 
                    placeholder="Fill logistic details & If For/Self pickup kindly mention the amount" 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                />
            </div>

            {/* --- INSTALLATION DETAILS --- */}
            <div style={{...cardStyle, padding: isMobile ? '15px' : '20px'}}>
                <h3>Installation Details</h3>
                <textarea 
                    name="installationDetails" 
                    value={formData.installationDetails} 
                    onChange={(e) => setFormData({...formData, installationDetails: e.target.value})} 
                    placeholder="Please input installation details." 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                />
            </div>

            {/* --- MATERIAL CHECKLIST --- */}
            <div style={{...cardStyle, padding: isMobile ? '15px' : '20px'}}>
                <h3>Material Checklist</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                        <input type="checkbox" checked={materialChecklistState.stickers} onChange={(e) => setMaterialChecklistState({...materialChecklistState, stickers: e.target.checked})} style={{ marginRight: '10px' }} />
                        Stickers
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                        <input type="checkbox" checked={materialChecklistState.laserMarking} onChange={(e) => setMaterialChecklistState({...materialChecklistState, laserMarking: e.target.checked})} style={{ marginRight: '10px' }} />
                        Laser Marking
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                        <input type="checkbox" checked={materialChecklistState.materialWrapping} onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialWrapping: e.target.checked})} style={{ marginRight: '10px' }} />
                        Material Wrapping
                    </label>
                </div>
                <textarea 
                    name="materialInstructions" 
                    value={materialChecklistState.materialInstructions} 
                    onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialInstructions: e.target.value})} 
                    placeholder="Please add any additional instructions here" 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                />
            </div>
        </div>

        {/* --- BILL OF MATERIAL GENERATOR --- */}
        <div style={{...cardStyle, marginTop: '20px'}}>
            <h3>Bill of Material Generator</h3>
            {boms.map((bom, index) => (
                <div key={bom.id} style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>{bom.name}</h4>
                        <div>
                            <button onClick={() => {
                                setCurrentBom(bom);
                                setShowBomModal(true);
                            }} style={{...linkBtnStyle, marginRight: '10px'}}><Edit2 size={14}/> Edit</button>
                            <button onClick={() => {
                                if (window.confirm("Are you sure you want to delete this Bill of Material (BOM)? This will remove all its components from the order.")) {
                                    setBoms(boms.filter(b => b.id !== bom.id));
                                }
                            }} style={{...linkBtnStyle, color: '#ef4444'}}><Trash2 size={14}/> Delete</button>
                        </div>
                    </div>
                    <p style={{fontSize: '12px', color: '#6b7280'}}>{bom.components.length} components</p>
                </div>
            ))}
            <button onClick={() => {
                setCurrentBom({ id: Date.now(), name: `BOM ${boms.length + 1}`, components: [], multiplier: 1 });
                setShowBomModal(true);
            }} style={btnPrimaryStyle}>+ Add New BOM</button>
        </div>

      {/* --- BOM MODAL --- */}
      {showBomModal && (
        <div style={modalOverlayStyle}>
          <div style={{...modalContentStyle, width:'85vw', maxWidth: '1400px', height: '90vh'}}>
            {/* HEADER */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '15px', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px' }}>BOM Editor: <span style={{color: '#2563eb'}}>{currentBom.name}</span></h2>
              <button onClick={() => setShowBomModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>

            {/* MAIN CONTENT */}
            <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '380px 1fr', gap: '20px', overflow: isMobile ? 'auto' : 'hidden' }}>
              
              {/* Left Panel: Controls */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflowY: isMobile ? 'visible' : 'auto', padding: '5px' }}>
                  <div style={{...cardStyle, padding: '15px'}}>
                      <label style={labelStyle}>BOM Name</label>
                      <input type="text" value={currentBom.name} onChange={(e) => setCurrentBom({...currentBom, name: e.target.value})} style={inputStyle} />
                  </div>
                  
                  <div style={{...cardStyle, padding: '15px'}}>
                      <label style={labelStyle}>Search Presets/Components</label>
                      <div style={{ position: 'relative' }} ref={bomSearchRef}>
                          <input type="text" placeholder="Search by name or SKU..." value={bomSearchTerm} 
                              onFocus={() => setShowBomSearchSuggestions(true)}
                              onChange={(e) => {
                                  setBomSearchTerm(e.target.value);
                                  if (e.target.value.length > 1) {
                                      setBomFilteredProducts(bomDatabase.filter(p => 
                                          p.name.toLowerCase().includes(e.target.value.toLowerCase()) || 
                                          p.sku.toLowerCase().includes(e.target.value.toLowerCase())
                                      ));
                                      setShowBomSearchSuggestions(true);
                                  } else {
                                      setBomFilteredProducts([]);
                                      setShowBomSearchSuggestions(false);
                                  }
                              }} style={inputStyle} />
                          {(bomFilteredProducts.length > 0 && showBomSearchSuggestions) && (
                              <div style={{...dropdownStyle, maxHeight: '300px', width: '100%'}}>
                                  {bomFilteredProducts.map(p => (
                                      <div key={p.sku} style={dropdownItemStyle} onClick={() => {
                                          let componentsToAdd = [];
                                          if (p.type === 'preset') {
                                              componentsToAdd = p.presetComponents.map(pc => {
                                                  const component = bomDatabase.find(c => c.sku === pc.sku);
                                                  return {...component, baseQty: pc.qty, id: `${p.sku}-${pc.sku}-${Date.now()}`};
                                              });
                                          } else {
                                              componentsToAdd.push({...p, baseQty: 1, id: `${p.sku}-${Date.now()}`});
                                          }

                                          const newComponents = [...currentBom.components];
                                          componentsToAdd.forEach(newComp => {
                                            const existingIndex = newComponents.findIndex(c => c.sku === newComp.sku);
                                            if (existingIndex > -1) {
                                              newComponents[existingIndex].baseQty += newComp.baseQty;
                                            } else {
                                              newComponents.push(newComp);
                                            }
                                          });

                                          setCurrentBom({...currentBom, components: newComponents});
                                          setBomSearchTerm('');
                                          setBomFilteredProducts([]);
                                          setShowBomSearchSuggestions(false);
                                      }}>
                                          <strong>{p.name}</strong> ({p.sku}) - <span style={{textTransform: 'capitalize', color: '#6b7280', fontSize:'12px'}}>{p.type}</span>
                                      </div>
                                  ))}
                              </div>
                          )}
                      </div>
                  </div>

                  <div style={{...cardStyle, padding: '15px'}}>
                      <label style={labelStyle}>Multiplier</label>
                      <input 
                          type="number" 
                          value={currentBom.multiplier}
                          min="0"
                          onChange={(e) => setCurrentBom({...currentBom, multiplier: parseFloat(e.target.value) || 1})}
                          style={inputStyle}
                      />
                      <p style={{fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: '1.5'}}>Multiplier will be applied to the base quantity of all components in this BOM.</p>
                  </div>
              </div>

              {/* Right Panel: Component List */}
              <div style={{ display: 'flex', flexDirection: 'column', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', overflow:'hidden', minHeight: isMobile ? '400px' : 'auto' }}>
                  <div style={{ padding: '12px 15px', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                      <h4 style={{ margin: 0, fontSize: '16px' }}>Components ({currentBom.components.length})</h4>
                  </div>

                  {currentBom.components.length > 0 ? (
                    <div style={{ overflowX: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : 'auto' }}>
                            <thead style={{position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1}}>
                                <tr>
                                    <th style={{...thStyle, textAlign: 'left', paddingLeft: '15px'}}>Name</th>
                                    <th style={{...thStyle, width: '120px'}}>SKU</th>
                                    <th style={{...thStyle, width: '120px'}}>Base Qty</th>
                                    <th style={{...thStyle, width: '120px'}}>Total Qty</th>
                                    <th style={{...thStyle, width: '80px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentBom.components.map((c, index) => (
                                    <tr key={c.id || index} style={{borderBottom: '1px solid #e5e7eb'}}>
                                        <td style={{...tdStyle, textAlign: 'left', paddingLeft: '15px'}}>
                                            <span style={{fontWeight: '500'}}>{c.name}</span>
                                        </td>
                                        <td style={{...tdStyle, color: '#6b7280'}}>{c.sku}</td>
                                        <td style={tdStyle}>
                                            <input type="number" value={c.baseQty} min="0" style={{...inputStyle, textAlign: 'center', width: '70px', padding: '6px'}} onChange={(e) => {
                                                const newComponents = [...currentBom.components];
                                                newComponents[index].baseQty = parseInt(e.target.value, 10) || 0;
                                                setCurrentBom({...currentBom, components: newComponents});
                                            }}/>
                                        </td>
                                        <td style={{...tdStyle, fontWeight:'bold', fontSize: '14px'}}>
                                            {Math.round((c.baseQty * currentBom.multiplier) * 100) / 100}
                                        </td>
                                        <td style={tdStyle}>
                                            <button onClick={() => {
                                                const newComponents = currentBom.components.filter((_, i) => i !== index);
                                                setCurrentBom({...currentBom, components: newComponents});
                                            }} style={{...linkBtnStyle, color: '#ef4444'}}><Trash2 size={16}/></button>
                                        </td>
                                    </tr>
                                ))}
                                <tr style={{backgroundColor: '#f9fafb', fontWeight: 'bold'}}>
                                    <td colSpan="3" style={{...tdStyle, textAlign: 'right', paddingRight: '15px'}}>Total Quantity:</td>
                                    <td style={{...tdStyle, fontSize: '14px'}}>
                                        {currentBom.components.reduce((sum, c) => sum + Math.round((c.baseQty || 0) * currentBom.multiplier), 0)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                  ) : (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', flexDirection: 'column', gap: '10px' }}>
                          <FileText size={40} color="#9ca3af" />
                          <h4 style={{margin:0}}>No components yet</h4>
                          <p style={{margin:0, fontSize: '13px', maxWidth: '250px', textAlign: 'center', lineHeight: '1.5'}}>Search for presets or individual components to add them to the list.</p>
                      </div>
                  )}
              </div>
            </div>

            {/* FOOTER */}
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 15, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
               <button onClick={() => setShowBomModal(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', background: 'white', borderRadius: 6, cursor:'pointer', fontWeight: 500 }}>Cancel</button>
               <button onClick={() => {
                   const bomExists = boms.some(b => b.id === currentBom.id);
                   if (bomExists) {
                       setBoms(boms.map(b => b.id === currentBom.id ? currentBom : b));
                   } else {
                       setBoms([...boms, currentBom]);
                   }
                   setShowBomModal(false);
                   setCurrentBom(null);
               }} style={btnPrimaryStyle}>Save BOM</button>
            </div>
          </div>
        </div>
      )}

      {/* --- REUSABLE MODALS --- */}
      <NewCustomerModal 
        show={showModal} 
        onClose={() => { setShowModal(false); setCustomerDataToEdit(null); }} 
        onSave={(savedCust) => {
          setAllCustomers(prev => {
            const existingIndex = prev.findIndex(c => c.id === savedCust.id);
            if (existingIndex > -1) {
              return prev.map(c => c.id === savedCust.id ? savedCust : c);
            }
            return [...prev, savedCust];
          });
          selectCustomer(savedCust);
        }}
        db={db}
        customerData={customerDataToEdit}
        isMobile={isMobile}
      />

      <AddAddressModal
        show={showAddressModal}
        onClose={() => { setShowAddressModal(false); setAddressDataToEdit(null); }}
        onSave={(savedAddress, updatedAddresses) => {
          const updatedCustomer = { ...selectedCustomer, addresses: updatedAddresses };
          setAllCustomers(prev => prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
          if (savedAddress.address_type === 'Billing') {
            setFormData(prev => ({ ...prev, billingAddress: savedAddress }));
          } else {
            setFormData(prev => ({ ...prev, shippingAddress: savedAddress }));
          }
        }}
        db={db}
        customer={selectedCustomer}
        addressType={addressModalType}
        addressData={addressDataToEdit}
        isMobile={isMobile}
      />

      {showFileModal && (
        <div style={modalOverlayStyle}>
          <div style={{...modalContentStyle, width: '500px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '15px', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Manage File: <span style={{color: '#2563eb'}}>{activeFileLabel}</span></h3>
              <button onClick={() => setShowFileModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {formData[activeFileField] ? (
                    <div style={{background: '#f0fdf4', padding: '15px', borderRadius: '8px', border: '1px solid #bbf7d0'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                            <div style={{background: '#22c55e', color: 'white', padding: '8px', borderRadius: '50%'}}><Eye size={20}/></div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h4 style={{margin: 0, color: '#166534', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={getFileNameFromUrl(formData[activeFileField])}>
                                    {getFileNameFromUrl(formData[activeFileField])}
                                </h4>
                                <p style={{margin: 0, fontSize: '12px', color: '#15803d'}}>You can view or replace this file.</p>
                            </div>
                        </div>
                        <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                            <a 
                                href={formData[activeFileField]} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                style={{
                                    ...btnPrimaryStyle, 
                                    backgroundColor: '#16a34a', 
                                    textDecoration: 'none', 
                                    justifyContent: 'center', 
                                    fontSize: '13px',
                                    flex: 1
                                }}
                            >
                                <Eye size={16}/> View File
                            </a>
                            <button 
                                onClick={() => {
                                    if(window.confirm('Are you sure you want to remove this file?')) {
                                        setFormData({...formData, [activeFileField]: ''});
                                    }
                                }} 
                                style={{
                                    ...btnPrimaryStyle, 
                                    backgroundColor: '#ef4444', 
                                    justifyContent: 'center', 
                                    fontSize: '13px',
                                    flex: 1
                                }}
                            >
                                <Trash2 size={16}/> Remove
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{textAlign: 'center', padding: '30px 20px', border: '2px dashed #e5e7eb', borderRadius: '8px', background: '#f9fafb'}}>
                        <Paperclip size={40} color="#9ca3af" style={{marginBottom: '10px'}}/>
                        <h4 style={{margin: '0 0 5px 0', color: '#374151'}}>No File Attached</h4>
                        <p style={{margin: 0, fontSize: '13px', color: '#6b7280'}}>Upload a document to attach it to this order.</p>
                    </div>
                )}

                <div>
                    <label style={{...labelStyle, fontSize: '12px', marginBottom: '8px'}}>
                        {formData[activeFileField] ? 'Replace with New File' : 'Upload File'}
                    </label>
                    <div style={{position: 'relative'}}>
                        <input 
                            type="file" 
                            onChange={(e) => handleFileUpload(e, activeFileField)} 
                            style={{...inputStyle, padding: '12px', background: '#f9fafb'}} 
                            disabled={uploading} 
                        />
                        {uploading && (
                            <div style={{
                                position: 'absolute', 
                                top: 0, left: 0, right: 0, bottom: 0, 
                                background: 'rgba(255,255,255,0.8)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                gap: '10px',
                                fontWeight: 'bold',
                                color: '#2563eb'
                            }}>
                                <Loader2 className="animate-spin" size={20} /> Uploading...
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 15, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
               <button onClick={() => { setShowFileModal(false); setActiveFileField(null); }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', background: 'white', borderRadius: 5, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, maxHeight: '250px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 100 };
const dropdownItemStyle = { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' };
const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 9999 };
const modalContentStyle = { backgroundColor: 'white', width: '900px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const rowStyle = { display: 'flex', gap: '15px', marginBottom: '15px' };
const sectionHeaderStyle = { margin: '20px 0 10px 0', borderBottom:'1px solid #eee', paddingBottom:5, color:'#374151', fontSize: '14px', fontWeight:'bold' };
const readOnlyBoxStyle = { background: '#f8fafc', padding: '10px', borderRadius: '5px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', minHeight: '40px' };
const linkBtnStyle = { background:'none', border:'none', color:'#2563eb', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:4, padding:0 };
const thStyle = { padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '10px', fontSize: '13px', textAlign: 'center' };

export default CreateOrder;